import { useContext } from 'react'

import { HcCard } from '@hotmart-org-ca/cosmos-web/dist/react/hc-card'
import { HcDrawer } from '@hotmart-org-ca/cosmos-web/dist/react/hc-drawer'
import { HcMedia } from '@hotmart-org-ca/cosmos-web/dist/react/hc-media'
import { OptimizelyContext } from '@optimizely/react-sdk'
import Button from '@space-core/components/button'
import useTranslation from '@space-core/hooks/useTranslation'
import { ThemeMode } from '@space-core/styles/themes/enum'
import { loginEventDispatch } from '@space-events/login'
import productDefaultImage from '@space-shell/assets/images/productDefaultImage.svg'
import LockIcon from '@space-shell/common/components/lockIcon'
import WindowCloseIcon from '@space-shell/common/components/windowCloseIcon'
import useThemeContext from '@space-shell/common/hooks/useThemeContext'
import { useMembershipStore } from '@space-shell/core/store'
import useProductClick from '@space-shell/modules/home/hooks/useProductClick'
import { verifyIfIsAPhysicalProduct } from '@space-shell/modules/products/utils/physicalProduct'
import { useSalesPage } from '@space-shell/modules/sales/presentation/hooks/common/use-sales-page'
import styled from 'styled-components'

import useProduct from '../../context/hook'

const DrawerContainer = styled(HcDrawer)<{ $theme?: ThemeMode }>`
  --hc-drawer-header-padding: 0;
  --hc-drawer-mobile-full-dialog-border-radius: 8px 8px 0 0;
  --hc-drawer-dialog-background-color: ${({ $theme }) =>
    $theme === ThemeMode.DARK ? 'rgb(14, 16, 18)' : 'var(--white)'};
`

const Title = styled.p<{ $theme?: ThemeMode }>`
  font-size: 20px;
  color: ${({ $theme }) => ($theme === ThemeMode.DARK ? ' var(--white)' : 'rgb(14, 16, 18)')};
  font-weight: var(--font-weight-bold);
  margin-top: 24px;
`

const Category = styled.p`
  font-size: 16px;
  color: var(--gray-400);
  font-weight: var(--font-weight-bold);
  margin-top: 4px;
`

const Description = styled.p`
  font-size: 14px;
  color: var(--gray-600);
  line-height: 21px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-top: 12px;
`

const Info = styled.p`
  font-size: 14px;
  color: var(--gray-600);
  margin-top: 24px;
`

const addDefaultSrc = (e) => {
  e.target.src = productDefaultImage
}
const ProductDrawer = () => {
  const {
    t,
    i18n: { language }
  } = useTranslation()
  const { product, methods } = useProduct()
  const { membershipSlug } = useMembershipStore()
  const internalSalesPageUrl = useSalesPage({ product })
  const { optimizely } = useContext(OptimizelyContext)
  const { theme } = useThemeContext()
  const handleProductClick = useProductClick({ product })

  const {
    itemId,
    title,
    bwImage,
    category,
    description,
    purchased,
    joined,
    accessLink,
    productHotLink
  } = product

  if (!itemId) {
    return null
  }
  const isAPhysicalProduct = verifyIfIsAPhysicalProduct(String(category))

  const productHomeUrl = `/${language.toLocaleLowerCase()}/club/${membershipSlug}/products/${itemId}`
  const productUrl =
    !purchased && !joined && productHotLink
      ? internalSalesPageUrl || productHotLink
      : accessLink || productHomeUrl

  const handleBtnBuy = (e: React.MouseEvent<HTMLButtonElement>) => {
    optimizely?.track('click_buy_product')
    handleProductClick(e, productUrl)
  }

  const handleBtnStudent = (e: React.MouseEvent<HTMLButtonElement>) => {
    optimizely?.track('click_im_student')
    optimizely?.track('click_login_primary')
    e.stopPropagation()
    loginEventDispatch({ signInRedirect: true })
  }

  const handleClose = () => {
    optimizely?.track('click_close')
    methods.setProduct({ ...product, itemId: '' })
  }

  return (
    <DrawerContainer
      position='bottom'
      dismissible={false}
      mobileFull
      opened
      $theme={theme}
      onCloseDrawer={handleClose}>
      <div slot='heading'>
        <HcCard spacing='none'>
          <HcMedia slot='media' aspectRatio={'16-9'} size='full'>
            <img src={bwImage?.url} alt={title} onError={addDefaultSrc} />
            <div slot='content' className='_d-flex _justify-content-between'>
              <LockIcon />
              <WindowCloseIcon onClick={handleClose} />
            </div>
          </HcMedia>
        </HcCard>
      </div>
      <div slot='content'>
        <Title $theme={theme}>{title}</Title>
        <Category>{t(`products.${category}`)}</Category>
        <Description>{description}</Description>
        <div>
          <Button
            key='home.card_cta.purchase'
            variation='success'
            className='_w-full _mt-5'
            onClick={(e) => handleBtnBuy(e)}>
            {t('home.card_cta.purchase')}
          </Button>
          {!isAPhysicalProduct && (
            <Button
              key='home.card_cta.is_student'
              variation='primary'
              className='_w-full _mt-5'
              onClick={(e) => handleBtnStudent(e)}>
              {t('home.card_cta.is_student')}
            </Button>
          )}
        </div>
        <Info> {t('home.info_customer_login')}</Info>
      </div>
    </DrawerContainer>
  )
}

export default ProductDrawer
